import FitParser from "fit-file-parser";
import type { Ride } from "../types/ride";
import { isFitFile, parseRideBuffer } from "./fitParser";
import { createRideParserPhaseProfile, type RideParserPhaseProfile } from "./rideParserProfile";

export type RideUploadStage =
  | "idle"
  | "reading"
  | "validating"
  | "decompressing"
  | "parsing"
  | "building"
  | "analyzing"
  | "complete"
  | "failed"
  | "cancelled";

export type RideUploadProgress = {
  stage: RideUploadStage;
  label: string;
  progress: number;
};

export type RideParserDiagnostics = {
  parserTimingMs: number;
  recordCount: number;
  /** Decoded FIT message count (sum of top-level message groups), not decoded tree nodes. */
  messageCount: number;
  detectedSport: string;
  warnings: string[];
  parserVersion: string;
};

export type RideUploadTimingDiagnostics = {
  readingMs: number | null;
  validationMs: number | null;
  decompressionMs: number | null;
  parsingMs: number | null;
  buildingMs: number | null;
  analysisMs: number | null;
  totalMs: number;
};

export type RideUploadResult = {
  ride: Ride;
  diagnostics: RideParserDiagnostics;
  timingDiagnostics?: RideUploadTimingDiagnostics;
  parserProfile?: RideParserPhaseProfile;
};

export type RideUploadBinaryInput = {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  arrayBuffer: () => Promise<ArrayBuffer>;
};

export class RideUploadError extends Error {
  constructor(public readonly code: "empty-file" | "unsupported-file" | "read-failed" | "decompression-failed" | "parser-failed" | "invalid-ride" | "cancelled", message: string) {
    super(message);
    this.name = "RideUploadError";
  }
}

const STAGES: Record<Exclude<RideUploadStage, "idle" | "failed" | "cancelled">, Omit<RideUploadProgress, "stage">> = {
  reading: { label: "Reading file(s)...", progress: 12 },
  validating: { label: "Validating...", progress: 24 },
  decompressing: { label: "Decompressing FIT.gz...", progress: 36 },
  parsing: { label: "Parsing FIT...", progress: 52 },
  building: { label: "Building ride model...", progress: 72 },
  analyzing: { label: "Running analysis...", progress: 88 },
  complete: { label: "Analysis complete", progress: 100 }
};

async function emitStage(stage: keyof typeof STAGES, onProgress?: (progress: RideUploadProgress) => void) {
  onProgress?.({ stage, ...STAGES[stage] });
  if (onProgress && typeof requestAnimationFrame === "function") {
    await new Promise<void>(resolve => {
      let resolved = false;
      const finish = () => {
        if (resolved) return;
        resolved = true;
        resolve();
      };
      requestAnimationFrame(() => finish());
      setTimeout(() => finish(), 16);
    });
  }
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new RideUploadError("cancelled", "Ride import cancelled.");
  }
}

async function readFileAsArrayBuffer(file: File | RideUploadBinaryInput, signal?: AbortSignal): Promise<ArrayBuffer> {
  if (!signal) {
    return await file.arrayBuffer();
  }

  throwIfAborted(signal);
  if (file instanceof File) {
    const stream = file.stream();
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let totalLength = 0;

  const abortHandler = () => {
    void reader.cancel().catch(() => undefined);
  };

  signal.addEventListener("abort", abortHandler, { once: true });
  try {
    while (true) {
      throwIfAborted(signal);
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      if (!value) {
        continue;
      }
      chunks.push(value);
      totalLength += value.byteLength;
    }
  } finally {
    signal.removeEventListener("abort", abortHandler);
    reader.releaseLock();
  }

    const merged = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return merged.buffer;
  }

  return await file.arrayBuffer();
}

async function decompressGzip(buffer: ArrayBuffer): Promise<ArrayBuffer> {
  try {
    const stream = new Blob([buffer]).stream().pipeThrough(new DecompressionStream("gzip"));
    return await new Response(stream).arrayBuffer();
  } catch {
    throw new RideUploadError("decompression-failed", "This FIT.gz file could not be decompressed. It may be corrupted or incomplete.");
  }
}

function findSport(value: unknown): string | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const sport = findSport(item);
      if (sport) return sport;
    }
    return null;
  }
  const entry = value as Record<string, unknown>;
  const directSport = entry.sport ?? entry.sport_name ?? entry.sportName;
  if (typeof directSport === "string" && directSport.trim()) {
    return directSport;
  }
  for (const item of Object.values(entry)) {
    const sport = findSport(item);
    if (sport) return sport;
  }
  return null;
}

function verifyRide(ride: Ride): string[] {
  if (!ride.rideDate) {
    throw new RideUploadError("invalid-ride", "The FIT file was decoded, but it does not contain a ride start time.");
  }
  if (!ride.movingTime && !ride.elapsedTime) {
    throw new RideUploadError("invalid-ride", "The FIT file was decoded, but it does not contain a ride duration.");
  }
  if (ride.records.length === 0) {
    throw new RideUploadError("invalid-ride", "The FIT file was decoded, but it does not contain any activity records.");
  }

  const warnings: string[] = [];
  const hasValue = (selector: (record: Ride["records"][number]) => unknown) => ride.records.some(record => selector(record) !== null && selector(record) !== undefined);
  if (!ride.records.some(record => record.latitudeDegrees != null && record.longitudeDegrees != null)) warnings.push("No GPS points detected; this may be an indoor ride.");
  if (!hasValue(record => record.powerWatts)) warnings.push("No power data detected.");
  if (!hasValue(record => record.heartRateBpm)) warnings.push("No heart-rate data detected.");
  if (!hasValue(record => record.cadenceRpm)) warnings.push("No cadence data detected.");
  if (!hasValue(record => record.altitudeMeters) && ride.elevationGain == null) warnings.push("No elevation data detected.");
  return [...warnings, ...(ride.analyticsValidationWarnings ?? [])];
}

export async function runRideUploadPipeline(
  file: File | RideUploadBinaryInput,
  fitParser: FitParser,
  ftpWatts: number | null | undefined,
  onProgress?: (progress: RideUploadProgress) => void,
  options: { runAnalysis?: boolean; signal?: AbortSignal } = {}
): Promise<RideUploadResult> {
  const runAnalysis = options.runAnalysis ?? true;
  const signal = options.signal;
  const parserProfile = createRideParserPhaseProfile();
  const timingDiagnostics: RideUploadTimingDiagnostics = {
    readingMs: null,
    validationMs: null,
    decompressionMs: null,
    parsingMs: null,
    buildingMs: null,
    analysisMs: null,
    totalMs: 0
  };
  const overallStartedAt = performance.now();
  throwIfAborted(signal);
  const readingStartedAt = performance.now();
  await emitStage("reading", onProgress);
  timingDiagnostics.readingMs = Math.max(0, performance.now() - readingStartedAt);
  throwIfAborted(signal);
  const validationStartedAt = performance.now();
  await emitStage("validating", onProgress);
  timingDiagnostics.validationMs = Math.max(0, performance.now() - validationStartedAt);
  throwIfAborted(signal);
  if (file.size === 0) {
    throw new RideUploadError("empty-file", "The selected file is empty. Choose a FIT file that contains ride data.");
  }
  if (!isFitFile(file)) {
    throw new RideUploadError("unsupported-file", "Unsupported file type. Choose a .fit or .fit.gz file.");
  }

  let buffer: ArrayBuffer;
  try {
    buffer = await readFileAsArrayBuffer(file, signal);
  } catch {
    throwIfAborted(signal);
    throw new RideUploadError("read-failed", "The selected file could not be read. Check that it is available and try again.");
  }
  if (/\.fit\.gz$/i.test(file.name)) {
    throwIfAborted(signal);
    const decompressionStartedAt = performance.now();
    await emitStage("decompressing", onProgress);
    buffer = await decompressGzip(buffer);
    timingDiagnostics.decompressionMs = Math.max(0, performance.now() - decompressionStartedAt);
  }

  throwIfAborted(signal);
  const parsingStartedAt = performance.now();
  await emitStage("parsing", onProgress);
  const startedAt = performance.now();
  let ride: Ride;
  try {
    ride = await parseRideBuffer(buffer, fitParser, ftpWatts, runAnalysis, signal, parserProfile);
  } catch (error) {
    if (error instanceof RideUploadError && error.code === "cancelled") {
      throw error;
    }
    if (error instanceof Error && /cancelled/i.test(error.message)) {
      throw new RideUploadError("cancelled", "Ride import cancelled.");
    }
    throw new RideUploadError("parser-failed", "The FIT parser could not read this file. It may be corrupted or use an unsupported FIT profile.");
  }

  timingDiagnostics.parsingMs = Math.max(0, performance.now() - parsingStartedAt);
  throwIfAborted(signal);
  const buildingStartedAt = performance.now();
  await emitStage("building", onProgress);
  ride.sourceFileName = file.name;
  ride.sourceFileSizeBytes = file.size;
  const warnings = verifyRide(ride);
  timingDiagnostics.buildingMs = Math.max(0, performance.now() - buildingStartedAt);
  throwIfAborted(signal);
  let analysisStartedAt: number | null = null;
  if (runAnalysis) {
    analysisStartedAt = performance.now();
    await emitStage("analyzing", onProgress);
  }
  const parserTimingMs = performance.now() - startedAt;
  if (analysisStartedAt !== null) {
    timingDiagnostics.analysisMs = Math.max(0, performance.now() - analysisStartedAt);
  }
  timingDiagnostics.totalMs = Math.max(0, performance.now() - overallStartedAt);
  if (runAnalysis) {
    await emitStage("complete", onProgress);
  } else {
    onProgress?.({ stage: "complete", label: "Primary ride parsed", progress: 100 });
  }

  return {
    ride,
    diagnostics: {
      parserTimingMs,
      recordCount: ride.records.length,
      messageCount: parserProfile.counts.fitMessageCount ?? ride.records.length,
      detectedSport: findSport(ride.rawData) ?? "Unknown",
      warnings,
      parserVersion: "fit-file-parser 4.1.0"
    },
    timingDiagnostics,
    parserProfile
  };
}

export function getSelectedUpload(files: FileList | null): File | null {
  return files?.item(0) ?? null;
}